@if (loading()) {
  <div class="flex items-center gap-3 p-6 text-sm text-muted-foreground">
    <hlm-spinner size="20" /> Loading numbers...
  </div>
}

@if (error(); as err) {
  <div class="mx-auto max-w-[1400px]">
    <cmt-error-state
      title="Failed to load CC numbers"
      [message]="err"
      (retry)="loadAll()" />
  </div>
}

@if (!loading() && !error()) {
  <div class="mx-auto flex max-w-[1600px] flex-col gap-5">

    <!-- HERO CARD -->
    <div class="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <div class="flex flex-wrap items-start justify-between gap-4">
        <div class="flex min-w-0 flex-col gap-1">
          <p class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">
            Call-Center Numbers
          </p>
          <h1 class="font-display text-2xl font-semibold leading-tight text-foreground">
            Numbers, AM &amp; Waiting Queue
          </h1>
          <p class="text-sm text-muted-foreground">
            {{ callcenterNumbers().length }} number{{ callcenterNumbers().length !== 1 ? 's' : '' }} booked
          </p>
        </div>
      </div>
    </div>

    <!-- ============================================ -->
    <!-- Section 1: Callcenter Numbers                -->
    <!-- ============================================ -->
    <section class="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <header class="mb-4 flex items-center gap-2.5">
        <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
          <hlm-icon name="lucidePhone" size="1rem" />
        </span>
        <h2 class="font-display text-lg font-semibold text-foreground">Callcenter Numbers</h2>
        <span class="ml-1 rounded-full bg-muted px-2 py-0.5 font-mono-feat text-sm text-muted-foreground">
          {{ callcenterNumbers().length }}
        </span>
      </header>

      @if (callcenterNumbers().length > 0) {
        <!-- Customer-visibility notice: the Tags column is shown to the customer. -->
        <div class="mb-3 flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2">
          <hlm-icon name="lucideTriangleAlert" size="0.9rem" class="mt-0.5 flex-none text-warning" />
          <p class="text-sm text-foreground">
            Tags are visible to the customer — keep labels appropriate for customer eyes.
          </p>
        </div>

        <!-- Header row -->
        <div class="mb-1 grid grid-cols-[1fr_1.4fr_110px_160px_70px_170px] items-center gap-3 px-2 cmt-eyebrow">
          <span>Number</span>
          <span>Tags</span>
          <span>Type</span>
          <span>Answering Machine</span>
          <span>Status</span>
          <span></span>
        </div>

        <div class="flex flex-col divide-y divide-border/70">
          @for (n of callcenterNumbers(); track n.id) {
            <div class="grid grid-cols-[1fr_1.4fr_110px_160px_70px_170px] items-center gap-3 px-2 py-2.5">
              <!-- Number + copy -->
              <div class="flex min-w-0 items-center gap-2">
                <span class="font-mono-feat text-sm text-foreground">{{ splitNumber(n) }}</span>
                <button hlmBtn variant="ghost" size="icon"
                  class="h-7 w-7"
                  (click)="copyToClipboard(n.number, $event)"
                  hlmTooltip="Copy number">
                  <hlm-icon name="lucideCopy" size="0.8rem" />
                </button>
                <button hlmBtn variant="outline" size="sm"
                  class="ml-auto h-7 gap-1 text-sm"
                  [ngClass]="isVoxboneError(n)
                    ? 'border-destructive/50 text-destructive hover:bg-destructive/10 hover:border-destructive hover:text-destructive'
                    : 'hover:bg-primary/10 hover:border-primary hover:text-primary'"
                  (click)="openDetails(n)"
                  hlmTooltip="Show full technical details">
                  <hlm-icon name="lucideInfo" size="0.8rem" /> Full Info
                </button>
              </div>

              <!-- Tags: read-only chips; editable (add/remove) when the row is in edit mode -->
              <div class="flex min-w-0 flex-wrap items-center gap-1">
                @if (editingRowId() === n.id) {
                  @for (t of n.tags; track t.id) {
                    <!-- Same tag badge as the read-only chip, with a trash affordance appended -->
                    <hlm-badge variant="tag" class="gap-1 !pr-1">
                      <hlm-icon name="lucideTag" size="0.75rem" />{{ t.name }}
                      <button type="button" class="grid h-5 w-5 place-items-center rounded-full text-muted-foreground hover:bg-destructive/15 hover:text-destructive disabled:opacity-50"
                        (click)="confirmRemoveTag(n, t)" [disabled]="saving()"
                        [attr.aria-label]="'Remove tag ' + t.name" hlmTooltip="Remove tag">
                        <hlm-icon name="lucideTrash2" size="0.85rem" />
                      </button>
                    </hlm-badge>
                  }
                  <input hlmInput class="h-7 w-24" placeholder="new tag…"
                    [ngModel]="newTagName()" (ngModelChange)="newTagName.set($event)"
                    (keydown.enter)="addNumberTag(n)" [disabled]="saving()" aria-label="Add tag" />
                  <button hlmBtn [variant]="newTagName().trim() ? 'default' : 'outline'" size="sm" class="h-7 gap-1 text-sm"
                    (click)="addNumberTag(n)" [disabled]="saving() || !newTagName().trim()">
                    <hlm-icon name="lucidePlus" size="0.75rem" /> Add
                  </button>
                  <span class="mt-0.5 flex w-full items-center gap-1 text-sm text-muted-foreground">
                    <hlm-icon name="lucideTriangleAlert" size="0.8rem" /> Changes save immediately
                  </span>
                } @else {
                  @for (t of n.tags; track t.id) {
                    <hlm-badge variant="tag" class="gap-1"><hlm-icon name="lucideTag" size="0.75rem" />{{ t.name }}</hlm-badge>
                  } @empty {
                    <span class="text-sm text-muted-foreground">—</span>
                  }
                }
              </div>

              <!-- Type: inline edit or badge (Type is not editable for SERVICE numbers) -->
              <div>
                @if (editingRowId() === n.id && n.type !== 'SERVICE') {
                  <hlm-select
                    [options]="[{value: 'CALLCENTER', label: 'CALLCENTER'}, {value: 'HIDDEN', label: 'HIDDEN'}]"
                    [value]="n.type"
                    (valueChange)="changeType(n, $any($event) || 'CALLCENTER')"
                  />
                } @else {
                  <hlm-badge [variant]="n.type === 'HIDDEN' ? 'secondary' : n.type === 'SERVICE' ? 'accent' : 'default'">
                    {{ n.type }}
                  </hlm-badge>
                }
              </div>

              <!-- AM state per number, on one line. The state marker is a filled badge
                   in a fixed slot at the start of the cell, so the markers line up on one
                   vertical axis down the table instead of shifting with the button width
                   (green = the answering machine answers this number, red = it does not).
                   The action stays a labeled mini-button — it mutates live customer data
                   (Style Guide §13 / §16 #11b). -->
              <div class="flex items-center gap-2">
                @if (isAmLinked(n)) {
                  <span class="grid h-5 w-5 flex-none place-items-center rounded-full bg-success text-primary-foreground"
                    role="img"
                    [attr.aria-label]="'Answering machine active for ' + n.number"
                    hlmTooltip="Answering machine active for this number">
                    <hlm-icon name="lucidePower" size="0.7rem" />
                  </span>
                  @if (canDeactivateAm(n)) {
                    <button hlmBtn variant="outline" size="sm"
                      class="h-7 text-sm border-destructive/40 text-destructive hover:bg-destructive/10 hover:border-destructive hover:text-destructive"
                      (click)="deactivateAm(n)"
                      [disabled]="saving() || editingRowId() !== null"
                      [attr.aria-label]="'Deactivate answering machine for ' + n.number"
                      hlmTooltip="Deactivate for this number only — the answering machine stays booked for the phone service">
                      Deactivate
                    </button>
                  }
                } @else if (amActive() && (n.type === 'CALLCENTER' || n.type === 'HIDDEN')) {
                  <span class="grid h-5 w-5 flex-none place-items-center rounded-full bg-destructive text-primary-foreground"
                    role="img"
                    [attr.aria-label]="'Answering machine not active for ' + n.number"
                    hlmTooltip="Answering machine not active for this number">
                    <hlm-icon name="lucidePowerOff" size="0.7rem" />
                  </span>
                  <!-- The marker shows the state either way; only the action is gated, the same
                       way Deactivate is above. A number whose Voxbone regulation was rejected or
                       is still pending offers neither action (PR #42 review H3). -->
                  @if (canActivateAm(n)) {
                    <button hlmBtn variant="outline" size="sm" class="h-7 text-sm hover:bg-primary/10 hover:border-primary hover:text-primary"
                      (click)="activateAmForNumber(n)"
                      [disabled]="saving() || editingRowId() !== null"
                      [attr.aria-label]="'Activate answering machine for ' + n.number"
                      hlmTooltip="Activate the answering machine for this number">
                      Activate
                    </button>
                  }
                }
              </div>

              <!-- Voxbone status indicator. Rejected/failed → fix dialog directly; pending → read-only
                   details; plain "OK" stays non-interactive. -->
              <div>
                @if (isVoxboneError(n)) {
                  <!-- Rejected/failed → explicit, alert-coloured Fix action straight to the fix dialog. -->
                  <button hlmBtn variant="destructive" size="sm" class="h-7 gap-1"
                    (click)="openFixRegulation(n)"
                    [hlmTooltip]="voxboneTooltip(n)"
                    aria-label="Regulation rejected — open fix dialog">
                    <hlm-icon name="lucideWrench" size="0.8rem" /> Fix
                  </button>
                } @else if (isVoxbonePending(n)) {
                  <button hlmBtn variant="ghost" size="icon" class="h-7 w-7"
                    (click)="openDetails(n)"
                    [hlmTooltip]="voxboneTooltip(n)"
                    aria-label="Show number details">
                    <hlm-icon name="lucideCircleAlert" size="1rem" class="text-accent" />
                  </button>
                } @else {
                  <hlm-badge variant="success">OK</hlm-badge>
                }
              </div>

              <!-- Actions: labeled mini-buttons instead of icon-only.
                   Right-aligned so Delete keeps its column position even when
                   the Edit button is absent (e.g. SERVICE numbers). -->
              <div class="flex items-center justify-end gap-1">
                @if (editingRowId() === n.id) {
                  <button hlmBtn variant="default" size="sm" class="h-7 text-sm" (click)="closeRowEdit()" [disabled]="saving()">Done</button>
                } @else {
                  <button hlmBtn variant="outline" size="sm" class="h-7 text-sm hover:bg-primary/10 hover:border-primary hover:text-primary hover:shadow-sm"
                    (click)="startRowEdit(n)"
                    [disabled]="editingRowId() !== null">
                    <hlm-icon name="lucidePencil" size="0.75rem" /> Edit
                  </button>
                  <button hlmBtn variant="destructive" size="sm" class="h-7 text-sm"
                    (click)="confirmDeleteNumber(n)" [disabled]="editingRowId() !== null">
                    <hlm-icon name="lucideTrash2" size="0.9rem" /> Delete
                  </button>
                }
              </div>
            </div>
          }
        </div>
      } @else {
        <div class="rounded-2xl border border-dashed border-border bg-card/50 p-12 text-center text-sm text-muted-foreground">
          No callcenter numbers configured
        </div>
      }

      <div class="mt-4 flex items-center gap-2">
        <button hlmBtn variant="default" (click)="openBookNumberDialog()" [disabled]="editingRowId() !== null || saving()">
          <hlm-icon name="lucidePlus" size="0.9rem" /> Add Number
        </button>
      </div>
    </section>

    <!-- ============================================ -->
    <!-- Section 2: Answering Machine                 -->
    <!-- ============================================ -->
    <section class="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <header class="mb-4 flex items-center gap-2.5">
        <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
          <hlm-icon name="lucideMessageSquare" size="1rem" />
        </span>
        <h2 class="font-display text-lg font-semibold text-foreground">Answering Machine</h2>
      </header>

      <dl class="flex flex-col divide-y divide-border/70">
        <div class="flex items-center justify-between gap-4 py-2.5">
          <dt class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">Status</dt>
          <dd>
            @if (amActive()) {
              <hlm-badge variant="success">
                <span class="h-1.5 w-1.5 rounded-full bg-current"></span>
                Active
              </hlm-badge>
            } @else {
              <hlm-badge variant="secondary">
                <span class="h-1.5 w-1.5 rounded-full bg-current"></span>
                Inactive
              </hlm-badge>
            }
          </dd>
        </div>

        <!-- AM details — also rendered when the AM-info endpoint returns an empty
             body for a PS that does have a voicebox (verified backend gap, EB-3872):
             old CMT still shows the (empty) language select and the Public-number
             default in that case, and the language PUT works regardless. -->
        @if (amActive()) {
          <div class="flex items-center justify-between gap-4 py-2.5">
            <dt class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">Voicebox number type</dt>
            <dd class="text-sm font-medium text-foreground">{{ amInfo()?.withDummyNumber ? 'Dummy number' : 'Public number' }}</dd>
          </div>
          <div class="flex flex-col gap-2 py-2.5">
            <div class="flex items-center justify-between gap-4">
              <dt class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">Language</dt>
              <dd class="min-w-0 flex-1 text-right">
                <hlm-select
                  [options]="languageSelectOptions()"
                  [value]="amInfo()?.language ?? null"
                  placeholder="Select language…"
                  (valueChange)="updateAmLanguage($any($event) || '')"
                />
              </dd>
            </div>
            <!-- Test env only: the AM runs on a separate system there while the test DB
                 is a days-old live copy — the shown language can be stale/wrong.
                 Same banner pattern as the Call Details mocked-transcript note. -->
            @if (isTestEnvironment) {
              <div class="flex items-start gap-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2">
                <hlm-icon name="lucideTriangleAlert" size="0.9rem" class="mt-0.5 flex-none text-warning" />
                <p class="text-sm text-foreground">
                  Test environment: it is possible to see wrong data / wrong behavior here
                  due to the test environment.
                </p>
              </div>
            }
          </div>
          @if (amVoiceboxDisplayNumber(); as voiceboxDisplay) {
            <div class="flex items-center justify-between gap-4 py-2.5">
              <dt class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">Voicebox</dt>
              <dd class="flex items-center gap-2">
                <span class="font-mono-feat text-sm text-foreground">{{ voiceboxDisplay }}</span>
                <button hlmBtn variant="ghost" size="icon"
                  class="h-7 w-7"
                  (click)="copyToClipboard(voiceboxDisplay, $event)"
                  hlmTooltip="Copy number">
                  <hlm-icon name="lucideCopy" size="0.8rem" />
                </button>
              </dd>
            </div>
          }
        }
      </dl>

      @if (amActive()) {
        <div class="mt-4 flex flex-wrap items-center gap-2">
          <button hlmBtn variant="destructive" (click)="cancelAm()" [disabled]="saving()">
            <hlm-icon name="lucideTrash2" size="0.9rem" /> Cancel AM (all numbers)
          </button>
        </div>
      }

      @if (!amActive()) {
        @if (callcenterNumbers().length > 0) {
          <div class="mt-4 flex items-center gap-2">
            <button hlmBtn variant="default" (click)="openAmBookDialog()" [disabled]="saving()">
              <hlm-icon name="lucideMessageSquare" size="0.9rem" /> Book Answering Machine
            </button>
          </div>
        } @else {
          <div class="mt-3 rounded-2xl border border-dashed border-border bg-card/50 p-8 text-center text-sm text-muted-foreground">
            No answering machine active. Add a callcenter number first, then book an answering machine for it.
          </div>
        }
      }
    </section>

    <!-- ============================================ -->
    <!-- Section 3: Waiting Queue                     -->
    <!-- ============================================ -->
    <section class="rounded-2xl border border-border bg-card p-5 shadow-sm">
      <header class="mb-4 flex items-center gap-2.5">
        <span class="grid h-8 w-8 place-items-center rounded-lg bg-primary/10 text-primary">
          <hlm-icon name="lucideTimer" size="1rem" />
        </span>
        <h2 class="font-display text-lg font-semibold text-foreground">Waiting Queue</h2>
      </header>

      <dl class="flex flex-col divide-y divide-border/70">
        <div class="flex items-center justify-between gap-4 py-2.5">
          <dt class="font-mono-feat text-sm font-semibold uppercase tracking-[0.10em] text-muted-foreground">Status</dt>
          <dd>
            @if (wqStatus(); as wq) {
              <hlm-badge [variant]="wq.status === 'ACTIVE' ? 'success' : wq.status === 'REPAIR' ? 'destructive' : 'secondary'">
                <span class="h-1.5 w-1.5 rounded-full bg-current"></span>
                {{ wq.status | titlecase }}
              </hlm-badge>
            } @else {
              <hlm-badge variant="secondary">Unknown</hlm-badge>
            }
          </dd>
        </div>
      </dl>

      @if (nonVoiceboxCount() === 0) {
        <div class="mt-3 flex items-center gap-2 rounded-lg border border-accent/30 bg-accent/10 p-3 text-sm text-foreground">
          <hlm-icon name="lucideTriangleAlert" size="1rem" class="text-accent" />
          <span>At least one non-VOICEBOX number is required to use the waiting queue.</span>
        </div>
      }

      <div class="mt-4 flex flex-wrap items-center gap-2">
        @if (wqStatus()?.status !== 'ACTIVE') {
          <button hlmBtn variant="default" (click)="confirmActivateWq()" [disabled]="!canActivateWq() || saving()">
            <hlm-icon name="lucidePlay" size="0.9rem" /> Activate
          </button>
        }
        @if (wqStatus()?.status === 'ACTIVE' || wqStatus()?.status === 'REPAIR') {
          <button hlmBtn variant="destructive" (click)="confirmDeactivateWq()" [disabled]="saving()">
            <hlm-icon name="lucideSquare" size="0.9rem" /> Deactivate
          </button>
        }
      </div>
    </section>

  </div>
}
